/**
 * `<BulkBasicControlsDialog>` — the multi-add "uses basic controls" notice (ENG-4942).
 *
 * Multi-add skips the brand/model step, so D1 (`basic-controls-dialog.tsx`) and
 * C2 ("isn't supported yet") never showed for it. Before a bulk save, this ONE
 * dialog lists every selected camera that will run on the generic driver
 * (`listBasicControlsCameras`, bulk.ts) with D1's copy, and names a recognized
 * camera the registry does not support yet as such. Continue saves the whole
 * selection; Back (or the header X / Escape) returns to it and saves nothing.
 *
 * `device_basic_controls_closed` fires once per listed camera with the outcome,
 * using the event's existing values: Continue is `continue`; Back, X and Escape
 * are `closed` — the event's "returned instead of keeping the camera" value
 * (constants/device-identification.ts). No new enum value is introduced.
 *
 * Copy names the product through `vendorName()` (via `basicControlsBody`) so
 * Hive builds read correctly.
 */
import { useAnalytics } from '#~/analytics/hooks/index.js';
import type { BasicControlsOutcome } from '#~/analytics/domains/device-identification.js';
import { createDeviceIdentificationTracker } from '#~/analytics/domains/device-identification.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/tag.js';
import * as Dialog from '@studio/design-system/dialog';
import type { ReactElement } from 'react';
import { basicControlsBody } from './basic-controls-dialog.jsx';
import type { BulkBasicControlsCamera } from './bulk.js';

export interface BulkBasicControlsDialogProps {
  open: boolean;
  /** The cameras that will run on the generic driver, in selection order. */
  cameras: readonly BulkBasicControlsCamera[];
  /** Continue: save the whole selection. */
  onContinue: () => void;
  /** Back, header X or Escape: return to the selection without saving. */
  onBack: () => void;
}

/** Title: the camera itself when there is one, otherwise the count. */
export const bulkBasicControlsTitle = (cameras: readonly BulkBasicControlsCamera[]): string =>
  cameras.length === 1
    ? `${cameras[0]!.label} uses basic controls`
    : `${cameras.length} cameras use basic controls`;

/** `<BulkBasicControlsDialog>` component. */
export const BulkBasicControlsDialog = ({
  open,
  cameras,
  onContinue,
  onBack
}: BulkBasicControlsDialogProps): ReactElement | null => {
  const { track } = useAnalytics();
  const tracker = createDeviceIdentificationTracker(track);

  if (!open || cameras.length === 0) return null;

  // One event per listed camera, so a breakdown by brand/model counts cameras,
  // not dialogs (ENG-4942).
  const record = (outcome: BasicControlsOutcome): void => {
    for (const camera of cameras) {
      tracker.trackDeviceBasicControlsClosed({
        brand_name: camera.brandName,
        model_name: camera.modelName,
        outcome
      });
    }
  };

  const handleContinue = (): void => {
    record('continue');
    onContinue();
  };

  const handleBack = (): void => {
    record('closed');
    onBack();
  };

  return (
    // Header X and Escape share Radix's onOpenChange(false): both are Back. The
    // design-system Content short-circuits Escape unless `closeOnEscape` is set,
    // and outside clicks stay blocked, so the camera list cannot be dismissed by
    // a stray click.
    <Dialog.Dialog open onOpenChange={(nextOpen) => !nextOpen && handleBack()}>
      <Dialog.Overlay>
        <Dialog.Content
          closeOnEscape
          className='fixed left-1/2 top-1/2 flex max-h-[80vh] w-[506px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'
        >
          <Dialog.Header
            title={
              <Dialog.Title {...DataSTag(Sel.AddDeviceIdentification, 'bulk-basic-controls-title')}>
                {bulkBasicControlsTitle(cameras)}
              </Dialog.Title>
            }
          />

          <div className='flex min-h-0 flex-col gap-16 overflow-y-auto px-40 py-24'>
            <p className='text-sm text-muted'>{basicControlsBody(cameras.length)}</p>

            <ul className='flex flex-col gap-8'>
              {cameras.map((camera) => (
                <li
                  key={camera.id}
                  className='flex flex-col text-sm'
                  {...DataSTag(
                    Sel.AddDeviceIdentification,
                    'bulk-basic-controls-camera',
                    camera.id
                  )}
                >
                  <span className='font-medium'>
                    {camera.label}
                    {camera.notYetSupported && (
                      <span
                        className='text-muted'
                        {...DataSTag(
                          Sel.AddDeviceIdentification,
                          'bulk-basic-controls-not-supported',
                          camera.id
                        )}
                      >
                        {' '}
                        — isn't supported yet
                      </span>
                    )}
                  </span>
                  {camera.name && camera.name !== camera.label && (
                    <span className='text-muted'>{camera.name}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <Dialog.Footer>
            <_Footer.Back
              onClick={handleBack}
              {...STagNew(Sel.AddDeviceIdentification, 'bulk-basic-controls-back')}
            />
            <_Footer.Next
              onClick={handleContinue}
              {...STagNew(Sel.AddDeviceIdentification, 'bulk-basic-controls-continue')}
            />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Dialog>
  );
};
